import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import {
  Plus, Trash2, Edit3, Save, X, CheckCircle, AlertCircle,
  Zap, Globe, Key, Bell, ChevronDown, ChevronUp, Send, ToggleLeft, ToggleRight
} from "lucide-react";
import toast from "react-hot-toast";
import { useConfirm } from "../common/ConfirmDialog";

const CRM_TYPES = [
  { value: "generic",    label: "Genérico (Webhook)" },
  { value: "hubspot",    label: "HubSpot" },
  { value: "salesforce", label: "Salesforce" },
  { value: "zoho",       label: "Zoho CRM" },
  { value: "pipedrive",  label: "Pipedrive" },
  { value: "freshdesk",  label: "Freshdesk" },
  { value: "custom",     label: "Personalizado" },
];

const ALL_EVENTS = [
  { key: "call.start",  label: "Llamada iniciada" },
  { key: "call.end",    label: "Llamada finalizada" },
  { key: "call.missed", label: "Llamada perdida" },
];

function Badge({ active }) {
  return active
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-900/40 text-green-400 border border-green-800/40"><CheckCircle size={10}/> Activa</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-800 text-muted border border-gray-700"><X size={10}/> Inactiva</span>;
}

function IntegrationForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: "", type: "generic", webhookUrl: "", apiKey: "", apiSecret: "",
    events: ["call.start", "call.end", "call.missed"],
    active: true,
    ...initial,
    // La API devuelve snake_case (webhook_url/api_key/api_secret); el form usa
    // camelCase. Sin este mapeo, ...initial no pisaba los defaults vacíos y
    // editar una integración existente siempre arrancaba con los campos en
    // blanco (lo que además disparaba el autofill del navegador, ver abajo).
    webhookUrl: initial.webhookUrl ?? initial.webhook_url ?? "",
    apiKey: initial.apiKey ?? initial.api_key ?? "",
    apiSecret: initial.apiSecret ?? initial.api_secret ?? "",
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleEvent = (ev) => set("events",
    form.events.includes(ev) ? form.events.filter(e => e !== ev) : [...form.events, ev]
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="form-label" htmlFor="crm-name">Nombre de la integración</label>
          <input id="crm-name" className="form-input" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Mi CRM" autoComplete="off"/>
        </div>
        <div>
          <label className="form-label" htmlFor="crm-type">Tipo de CRM</label>
          <select id="crm-type" className="form-input" value={form.type} onChange={e => set("type", e.target.value)}>
            {CRM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="form-label" htmlFor="crm-webhook">URL Webhook (recibe eventos de llamadas)</label>
        <div className="relative">
          <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"/>
          <input id="crm-webhook" className="form-input pl-8" value={form.webhookUrl} onChange={e => set("webhookUrl", e.target.value)}
            placeholder="https://tu-crm.com/webhook/llamadas" autoComplete="off"/>
        </div>
        <p className="text-xs text-muted mt-1">El PBX enviará un POST JSON a esta URL cuando ocurra un evento de llamada.</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="form-label" htmlFor="crm-api-key">API Key (opcional)</label>
          <div className="relative">
            <Key size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"/>
            <input id="crm-api-key" className="form-input pl-8" value={form.apiKey} onChange={e => set("apiKey", e.target.value)}
              placeholder="sk_live_xxx" type="password" autoComplete="new-password"/>
          </div>
          <p className="text-xs text-muted mt-1">Se envía como header <code className="text-blue-400">X-Api-Key</code>.</p>
        </div>
        <div>
          <label className="form-label" htmlFor="crm-api-secret">API Secret (opcional)</label>
          <div className="relative">
            <Key size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"/>
            <input id="crm-api-secret" className="form-input pl-8" value={form.apiSecret} onChange={e => set("apiSecret", e.target.value)}
              placeholder="secreto" type="password" autoComplete="new-password"/>
          </div>
        </div>
      </div>

      <div>
        <span className="form-label flex items-center gap-2"><Bell size={13}/> Eventos a notificar</span>
        <div className="flex gap-3 flex-wrap">
          {ALL_EVENTS.map(ev => (
            <label key={ev.key} className={[
              "flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm",
              form.events.includes(ev.key)
                ? "bg-blue-900/30 border-blue-700 text-blue-300"
                : "bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600"
            ].join(" ")}>
              <input type="checkbox" className="hidden" checked={form.events.includes(ev.key)}
                onChange={() => toggleEvent(ev.key)}/>
              <Zap size={12}/> {ev.label}
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-400">Estado:</span>
        <button onClick={() => set("active", !form.active)}
          className={["flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors",
            form.active ? "bg-green-900/30 border-green-700 text-green-300" : "bg-gray-800 border-gray-700 text-gray-400"
          ].join(" ")}>
          {form.active ? <ToggleRight size={16}/> : <ToggleLeft size={16}/>}
          {form.active ? "Activa" : "Inactiva"}
        </button>
      </div>

      <div className="flex gap-3 pt-2 border-t border-gray-800">
        <button onClick={() => onSave(form)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
          <Save size={14}/> Guardar
        </button>
        <button onClick={onCancel} className="btn-secondary text-sm">
          <X size={14}/> Cancelar
        </button>
      </div>
    </div>
  );
}

function IntegrationCard({ crm, onEdit, onDelete, onTest }) {
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleTest = async () => {
    setTesting(true);
    try {
      await onTest(crm.id);
      toast.success("Evento de prueba enviado correctamente");
    } catch (err) {
      toast.error("El webhook no respondió: " + (err.response?.data?.error || err.message));
    } finally {
      setTesting(false);
    }
  };

  const crmType = CRM_TYPES.find(t => t.value === crm.type)?.label || crm.type;

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-900/40 border border-blue-800/40 flex items-center justify-center">
            <Zap size={18} className="text-blue-400"/>
          </div>
          <div>
            <div className="font-medium text-gray-200">{crm.name}</div>
            <div className="text-xs text-muted">{crmType}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge active={crm.active}/>
          <button onClick={handleTest} disabled={testing || !crm.webhook_url}
            title="Enviar evento de prueba"
            className="p-1.5 text-muted hover:text-blue-400 disabled:opacity-30 transition-colors">
            <Send size={15}/>
          </button>
          <button onClick={() => onEdit(crm)} aria-label={`Editar integración ${crm.name}`} className="p-1.5 text-muted hover:text-gray-300 transition-colors">
            <Edit3 size={15}/>
          </button>
          <button onClick={() => onDelete(crm.id)} aria-label={`Eliminar integración ${crm.name}`} className="p-1.5 text-muted hover:text-red-400 transition-colors">
            <Trash2 size={15}/>
          </button>
          <button onClick={() => setOpen(o => !o)} aria-label={open ? "Contraer detalles" : "Expandir detalles"} aria-expanded={open} className="p-1.5 text-muted hover:text-gray-300">
            {open ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-gray-800 space-y-2">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-muted mb-0.5">Webhook URL</div>
              <div className="text-gray-300 truncate font-mono text-xs">{crm.webhook_url || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-muted mb-0.5">Eventos</div>
              <div className="flex flex-wrap gap-1">
                {(crm.events || []).map(ev => (
                  <span key={ev} className="px-2 py-0.5 bg-gray-800 rounded text-xs text-gray-400">{ev}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-3 p-3 bg-gray-800/50 rounded-lg">
            <p className="text-xs text-muted font-medium mb-2">Payload de ejemplo (call.start):</p>
            <pre className="text-xs text-green-400 overflow-x-auto">{JSON.stringify({
              event: "call.start", callId: "abc123", caller: "27210",
              callerName: "JMP", destination: "27211", direction: "outbound",
              timestamp: new Date().toISOString()
            }, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

export default function IntegrationsPage() {
  const confirm = useConfirm();
  const [integrations, setIntegrations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/integrations/crm").then(r => { setIntegrations(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (form) => {
    try {
      if (editing) {
        await api.put(`/integrations/crm/${editing.id}`, form);
        toast.success("Integración actualizada");
      } else {
        await api.post("/integrations/crm", form);
        toast.success("Integración creada");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Error guardando");
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Eliminar la integración', message: 'Esta acción no se puede deshacer.', confirmLabel: 'Eliminar', danger: true }))) return;
    await api.delete(`/integrations/crm/${id}`);
    toast.success("Eliminada");
    load();
  };

  const handleTest = async (id) => {
    await api.post(`/integrations/crm/${id}/test`);
  };

  const handleEdit = (crm) => {
    setEditing(crm);
    setShowForm(true);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Integraciones CRM</h1>
          <p className="text-sm text-muted mt-0.5">
            Conecta el PBX con tu CRM para notificaciones de llamadas y click-to-call
          </p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="btn-primary flex items-center gap-2">
          <Plus size={16}/> Nueva integración
        </button>
      </div>

      {/* Click-to-call info */}
      <div className="card mb-6 bg-blue-900/10 border-blue-800/40">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-900/40 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Globe size={16} className="text-blue-400"/>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-300 mb-1">API Click-to-Call</p>
            <p className="text-xs text-gray-400 mb-2">
              Tu CRM puede originar llamadas enviando un POST autenticado:
            </p>
            <code className="block text-xs text-green-400 bg-gray-900 rounded p-2 font-mono">
              POST /api/integrations/click-to-call<br/>
              Authorization: Bearer &lt;token&gt;<br/>
              {"{"} "from": "27210", "to": "3001234567", "crmContactId": "abc" {"}"}
            </code>
          </div>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="card mb-6 border-blue-800/40">
          <h2 className="text-lg font-semibold text-gray-200 mb-4">
            {editing ? "Editar integración" : "Nueva integración CRM"}
          </h2>
          <IntegrationForm
            initial={editing || {}}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {loading && <div className="text-center text-muted py-12">Cargando...</div>}
        {!loading && integrations.length === 0 && !showForm && (
          <div className="card text-center py-12">
            <Zap size={32} className="text-gray-700 mx-auto mb-3"/>
            <p className="text-muted mb-4">Sin integraciones CRM configuradas</p>
            <button onClick={() => setShowForm(true)} className="btn-primary text-sm">
              <Plus size={14}/> Agregar primera integración
            </button>
          </div>
        )}
        {integrations.map(crm => (
          <IntegrationCard key={crm.id} crm={crm}
            onEdit={handleEdit} onDelete={handleDelete} onTest={handleTest}/>
        ))}
      </div>
    </div>
  );
}
